import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  addEdge as RcFlowAddEdge,
  applyEdgeChanges, applyNodeChanges, type Connection,
  type Edge, type EdgeChange,
  type Node, type NodeChange,
  type XYPosition,
} from '@xyflow/react';
import { EdgeType } from 'automations/data/edgeTypes';
import { NodeType } from 'automations/data/nodeTypes';
import { generateNewNodePayload } from 'automations/utils/node';
import { fitView } from 'automations/utils/staticFlowUtil';
import { formatAutomationTree } from 'automations/utils/treeUtil';

export interface ReactFlowState {
  nodes: Node[];
  edges: Edge[];
  hoveredEdgeId: string | null;
}

const initialState: ReactFlowState = {
  nodes: [generateNewNodePayload()],
  edges: [],
  hoveredEdgeId: null,
};

const reactFlowSlice = createSlice({
  name: 'reactFlow',
  initialState,
  reducers: {
    addInitialData: (state, action: PayloadAction<{
      nodes: Node[];
      edges: Edge[];
    }>) => {
      const updatedNodes = formatAutomationTree(
        action.payload.nodes,
        action.payload.edges,
      );
      state.nodes = updatedNodes;
      state.edges = action.payload.edges;
    },
    addNodeWithPosition: (
      state,
      action: PayloadAction<{
        newNode: Node;
        sourceNodeId: string;
        edgeConfig?: Partial<Edge>;
      }>,
    ) => {
      const { newNode, sourceNodeId } = action.payload;

      state.nodes.push(newNode);
      state.edges.push({
        id: crypto.randomUUID(),
        source: sourceNodeId,
        target: newNode.id,
        type: action.payload.edgeConfig?.type ?? EdgeType.default,
        ...action.payload.edgeConfig,
      });

      // Determine the new layout positions with Dagre
      const updatedNodes = formatAutomationTree(
        state.nodes,
        state.edges,
      );

      // update all nodes
      state.nodes = updatedNodes;
      fitView({
        nodes: [{ id: newNode.id }],
      });
    },
    replaceFallbackNode: (state, action: PayloadAction<{ nodeId: string; newNode: Node }>) => {
      const nodeIndex = state.nodes.findIndex((node) => node.id === action.payload.nodeId);
      if (nodeIndex !== -1 && state.nodes[nodeIndex]) {
        state.nodes[nodeIndex] = action.payload.newNode;
      }

      fitView({
        nodes: [{ id: action.payload.newNode.id }],
      });
    },
    /**
     * Swap the node id with the new node id - Used when we are updating the server provided node id.
     * @param action - The action payload containing the old and new node ids
     */
    swapNodeId: (state, action: PayloadAction<{ oldNodeId: string; newNodeId: string | number }>) => {
      const { oldNodeId, newNodeId } = action.payload;
      const nodeIndex = state.nodes.findIndex((node) => node.id === oldNodeId);
      if (nodeIndex !== -1 && state.nodes[nodeIndex]) {
        state.nodes[nodeIndex] = {
          ...state.nodes[nodeIndex],
          id: newNodeId.toString(),
          data: {
            ...state.nodes[nodeIndex].data,
            oldNodeId,
          },
        };
      }

      state.edges.forEach((edge) => {
        if (edge.source === oldNodeId) edge.source = newNodeId.toString();
        if (edge.target === oldNodeId) edge.target = newNodeId.toString();
      });
    },
    incrementExecutionOrder: (state, action: PayloadAction<{ nodeId: string; count: number }>) => {
      const { nodeId, count } = action.payload;
      const node = state.nodes.find((n) => n.id === nodeId);

      if (node && typeof node.data.executionOrder === 'number') {
        node.data.executionOrder += count;
      }
    },

    onNodesChange: (state, action: PayloadAction<NodeChange[]>) => {
      state.nodes = applyNodeChanges(action.payload, state.nodes);
    },
    onEdgesChange: (state, action: PayloadAction<EdgeChange[]>) => {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    onConnect: (state, action: PayloadAction<Connection>) => {
      state.edges = RcFlowAddEdge(action.payload, state.edges);
    },
    updateNode: (state, action: PayloadAction<Node>) => {
      const nodeIndex = state.nodes.findIndex((node) => node.id === action.payload.id);
      state.nodes[nodeIndex] = action.payload;
    },
    deleteNode: (state, action: PayloadAction<string>) => {
      state.nodes = state.nodes.filter((node) => node.id !== action.payload);
      // todo: handle deleting edges properly based on current node position.
    },

    updateInitialNodePosition: (state, action: PayloadAction<XYPosition>) => {
      if (state.nodes.length > 0 && state.nodes[0]?.type === NodeType.newBlock) {
        state.nodes[0].position = action.payload;
      }
    },

    deleteAllNodes: () => initialState,

    addAdditionalOptions: (state, action: PayloadAction<{
      nodeId: string;
      additionalOptions: Record<string, unknown>;
    }>) => {
      const nodeIndex = state.nodes.findIndex((node) => node.id === action.payload.nodeId);
      if (nodeIndex !== -1 && state.nodes[nodeIndex]) {
        state.nodes[nodeIndex].data.additionalOptions = {
          ...(state.nodes[nodeIndex].data.additionalOptions ?? {}),
          ...action.payload.additionalOptions,
        };
      }
    },

    setNodeAdditionalPropertiesModalOpen: (state, action: PayloadAction<{
      nodeId: string;
      open: boolean;
    }>) => {
      const { nodeId, open } = action.payload;
      const nodeIndex = state.nodes.findIndex(
        (node) => node.id === nodeId || node.data.oldNodeId === nodeId,
      );

      if (nodeIndex !== -1 && state.nodes[nodeIndex]) {
        state.nodes[nodeIndex].data.menuOpen = open;
      }
    },
    setHoveredEdgeId: (state, action: PayloadAction<string | null>) => {
      state.hoveredEdgeId = action.payload;
    },

    addNodeBetweenExistingNodes: (state, action: PayloadAction<{
      sourceId: string;
      targetId: string;
      newNode: Node;
      edgeConfig?: Partial<Edge>;
    }>) => {
      const {
        sourceId, targetId, newNode, edgeConfig,
      } = action.payload;

      state.nodes.push(newNode);

      // delete the existing edge between parent and child
      state.edges = state.edges.filter(
        (edge) => !(edge.source === sourceId && edge.target === targetId),
      );

      state.edges.push({
        id: crypto.randomUUID(),
        source: sourceId,
        target: newNode.id,
        ...edgeConfig,
      });

      state.edges.push({
        id: crypto.randomUUID(),
        source: newNode.id,
        target: targetId,
        ...edgeConfig,
      });

      const updatedNodes = formatAutomationTree(
        state.nodes,
        state.edges,
      );
      state.nodes = updatedNodes;

      fitView({
        nodes: [{ id: newNode.id }],
      });
    },

    handleNodeDelete: (state, action: PayloadAction<string>) => {
      const nodeIdToDelete = action.payload;

      // Helper function to get all descendant node IDs
      const getDescendantNodeIds = (nodeId: string): string[] => {
        const childEdges = state.edges.filter((edge) => edge.source === nodeId);
        const childNodeIds = childEdges.map((edge) => edge.target);
        const descendantIds = [...childNodeIds];
        childNodeIds.forEach((childId) => {
          descendantIds.push(...getDescendantNodeIds(childId));
        });

        return descendantIds;
      };

      // Get all nodes that need to be deleted (current node + descendants)
      const nodesToDelete = [nodeIdToDelete, ...getDescendantNodeIds(nodeIdToDelete)];
      // Delete all edges connected to any of the nodes being deleted
      state.edges = state.edges.filter(
        (edge) => !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target),
      );
      // Delete all the nodes
      state.nodes = state.nodes.filter((node) => !nodesToDelete.includes(node.id));

      state.nodes = state.nodes.length ? formatAutomationTree(state.nodes, state.edges) : [generateNewNodePayload()];
      fitView({});
    },
  },
});

export const {
  addNodeWithPosition,
  onNodesChange,
  onEdgesChange,
  onConnect,
  updateNode,
  deleteNode,
  addInitialData,
  swapNodeId,
  incrementExecutionOrder,
  handleNodeDelete,

  updateInitialNodePosition,
  replaceFallbackNode,
  deleteAllNodes,
  addAdditionalOptions,
  setNodeAdditionalPropertiesModalOpen,
  setHoveredEdgeId,
  addNodeBetweenExistingNodes,
} = reactFlowSlice.actions;

export default reactFlowSlice.reducer;
